// Shared schema + (de)serialization for the Sketch tool's geometry_json.
// Pure / Cesium-free so it can be reused by the save path and the renderer
// and reasoned about in isolation. Cartesian3 <-> lat/lng conversion lives
// in the Cesium-aware callers (useSketch / promotion / renderer).
export const SKETCH_GEOMETRY_VERSION = 1;
// Opacity floor, shared by the pen panel's slider and the parse clamp so a
// hand-edited or malformed geometry can never produce a stroke that is
// invisible and unreachable by the canvas slider.
export const SKETCH_MIN_OPACITY = 0.1;
const isFiniteNum = (v) => typeof v === 'number' && Number.isFinite(v);
const isValidPoint = (p) => !!p && typeof p === 'object' && isFiniteNum(p.lat) && isFiniteNum(p.lng);
export const serializeSketch = (strokes) => JSON.stringify({ version: SKETCH_GEOMETRY_VERSION, strokes });
// Defensive parse: never throws. Drops malformed strokes/points and
// coerces missing fields to sane defaults so one bad row can't blank the
// whole canvas. A stroke needs >=2 valid points to be drawable.
// Every reader of a saved sketch goes through here, so the field rules
// (and the opacity clamp in particular) exist in exactly one place.
export const parseSketchGeometry = (json) => {
    let raw;
    try {
        raw = JSON.parse(json);
    }
    catch (_a) {
        return { version: SKETCH_GEOMETRY_VERSION, strokes: [] };
    }
    const rawStrokes = (raw && typeof raw === 'object' && Array.isArray(raw.strokes))
        ? raw.strokes
        : [];
    const strokes = [];
    for (const s of rawStrokes) {
        if (!s || typeof s !== 'object')
            continue;
        const points = Array.isArray(s.points) ? s.points.filter(isValidPoint) : [];
        if (points.length < 2)
            continue;
        strokes.push({
            tool: s.tool === 'brush' ? 'brush' : 'pen',
            color: typeof s.color === 'string' ? s.color : '#DC143C',
            width: isFiniteNum(s.width) && s.width > 0 ? s.width : 4,
            opacity: isFiniteNum(s.opacity) ? Math.min(1, Math.max(SKETCH_MIN_OPACITY, s.opacity)) : 1,
            points,
        });
    }
    return { version: isFiniteNum(raw.version) ? raw.version : SKETCH_GEOMETRY_VERSION, strokes };
};
